import { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRouteApi } from "@tanstack/react-router";

import { genderOptions, ProfileStatus, statusOptions } from "@/constants/profile";
import { useProfile } from "@/hooks";
import { editProfileSummaryFormSchema } from "@/schemas/profile";
import type { EditProfileSummaryFormData } from "@/types/profile";
import { Button, Checkbox, Input, Select, SelectableAccordion } from "@/ui";
import { formatLongDate, handleAxiosFieldErrors, typedObjectEntries } from "@/utils";
import { PREFERENCE_LABELS } from "@/utils/consts";
import { DataRow } from "../data-row";

const selectStatusOptions = statusOptions.filter((option) => {
  return option.value !== ProfileStatus.draft && option.value !== ProfileStatus.pending_activation;
});

export const ProfileSummary = () => {
  const [isEditing, setIsEditing] = useState(false);

  const { id } = getRouteApi("/_private/profiles/$id").useParams();

  const { data: profile } = useProfile().getProfile(id);
  const { mutate: updateProfileSummary } = useProfile().updateProfileSummary();

  const defaultValues: EditProfileSummaryFormData = {
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    email: profile?.email || "",
    phone: profile?.phone || "",
    birthDate: profile?.birthDate || "",
    gender: profile?.gender || "other",
    referredBy: profile?.origin ?? "",
    caregiver: profile?.isCaregiver || false,
    isTestUser: profile?.isTestUser || false,
    preferences: profile?.avoidedPreferences || {},
    status: profile?.status || "active",
    emailNotificationsEnabled: Boolean(profile?.emailsEnabledAt),
    smsNotificationsEnabled: Boolean(profile?.smsEnabledAt),
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<EditProfileSummaryFormData>({
    resolver: zodResolver(editProfileSummaryFormSchema),
    defaultValues,
  });

  const currentPreferences = watch("preferences");
  const status = watch("status");

  useEffect(() => {
    if (status === ProfileStatus.inactive) {
      setValue("emailNotificationsEnabled", false);
      setValue("smsNotificationsEnabled", false);
    }
  }, [setValue, status]);

  const onSubmit: SubmitHandler<EditProfileSummaryFormData> = (data) => {
    if (!profile) {
      return;
    }

    updateProfileSummary(
      { id: profile?.id, ...data },
      {
        onSuccess: () => {
          return setIsEditing(false);
        },
        onError: (error) => {
          handleAxiosFieldErrors<EditProfileSummaryFormData>(error, setError);
        },
      },
    );
  };

  const onCancel = () => {
    reset();
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        {!isEditing && (
          <Button
            onClick={() => {
              return setIsEditing(true);
            }}
            variant="tertiaryFilled"
          >
            Edit
          </Button>
        )}

        {isEditing ? (
          <div className="flex gap-3">
            <Button onClick={onCancel} variant="tertiaryPlain">
              Cancel
            </Button>

            <Button form="profile-summary-form" type="submit" variant="tertiaryFilled">
              Save
            </Button>
          </div>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
        <form id="profile-summary-form" onSubmit={handleSubmit(onSubmit)}>
          <DataRow title="First Name">
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => {
                return (
                  <Input
                    id="first-name"
                    {...getDisabledClasses(!isEditing)}
                    disabled={!isEditing}
                    error={errors.firstName?.message}
                    {...field}
                  />
                );
              }}
            />
          </DataRow>

          <DataRow title="Last Name">
            <Controller
              control={control}
              name="lastName"
              render={({ field }) => {
                return (
                  <Input
                    id="last-name"
                    {...getDisabledClasses(!isEditing)}
                    disabled={!isEditing}
                    error={errors.lastName?.message}
                    {...field}
                  />
                );
              }}
            />
          </DataRow>

          <DataRow title="Email">
            <Controller
              control={control}
              name="email"
              render={({ field }) => {
                return (
                  <Input
                    id="email"
                    {...getDisabledClasses(!isEditing)}
                    disabled={!isEditing}
                    error={errors.email?.message}
                    {...field}
                  />
                );
              }}
            />
          </DataRow>

          <DataRow title="Status">
            <Controller
              control={control}
              name="status"
              render={({ field }) => {
                return (
                  <Select
                    className="w-full"
                    error={errors.status?.message}
                    id="status"
                    options={selectStatusOptions}
                    {...field}
                    disabled={!isEditing}
                  />
                );
              }}
            />
          </DataRow>

          <DataRow title="Phone Number">
            <Controller
              control={control}
              name="phone"
              render={({ field }) => {
                return (
                  <Input
                    id="phone"
                    {...getDisabledClasses(!isEditing)}
                    disabled={!isEditing}
                    error={errors.phone?.message}
                    {...field}
                  />
                );
              }}
            />
          </DataRow>

          <DataRow title="Birth Date">
            {isEditing ? (
              <Controller
                control={control}
                name="birthDate"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Input
                      className="w-full"
                      error={errors.birthDate?.message}
                      id="birth-date"
                      onChange={onChange}
                      type="date"
                      value={value ?? undefined}
                    />
                  );
                }}
              />
            ) : (
              `${profile?.birthDate} (${profile?.age} years)`
            )}
          </DataRow>

          <DataRow title="Gender">
            {isEditing ? (
              <Controller
                control={control}
                name="gender"
                render={({ field }) => {
                  return (
                    <Select
                      className="w-full"
                      error={errors.gender?.message}
                      id="gender"
                      options={genderOptions}
                      {...field}
                    />
                  );
                }}
              />
            ) : (
              profile?.gender
            )}
          </DataRow>

          <DataRow title="Referred By">
            {isEditing ? (
              <Controller
                control={control}
                name="referredBy"
                render={({ field }) => {
                  return (
                    <Input
                      className="w-full"
                      error={errors.referredBy?.message}
                      id="origin"
                      {...field}
                    />
                  );
                }}
              />
            ) : (
              (profile?.origin ?? "-")
            )}
          </DataRow>

          <DataRow title="Caregiver">
            {isEditing ? (
              <Controller
                control={control}
                name="caregiver"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Checkbox
                      checked={value}
                      error={errors.caregiver?.message}
                      id="caregiver"
                      onChange={onChange}
                    />
                  );
                }}
              />
            ) : (
              <span>{profile?.isCaregiver ? "Yes" : "No"}</span>
            )}
          </DataRow>

          <DataRow title="Test Profile">
            {isEditing ? (
              <Controller
                control={control}
                name="isTestUser"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Checkbox
                      checked={value}
                      error={errors.isTestUser?.message}
                      id="is-test-user"
                      onChange={onChange}
                    />
                  );
                }}
              />
            ) : (
              <span>{profile?.isTestUser ? "Yes" : "No"}</span>
            )}
          </DataRow>

          <DataRow title="Receive Email Notifications">
            {isEditing ? (
              <Controller
                control={control}
                name="emailNotificationsEnabled"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Checkbox
                      checked={value}
                      disabled={status === ProfileStatus.inactive}
                      disabledLegend="To update email notifications, you must activate the patient first"
                      error={errors.emailNotificationsEnabled?.message}
                      id="email-notifications"
                      onChange={onChange}
                    />
                  );
                }}
              />
            ) : (
              <span>{profile?.emailsEnabledAt ? "Yes" : "No"}</span>
            )}
          </DataRow>

          <DataRow title="Receive SMS Notifications">
            {isEditing ? (
              <Controller
                control={control}
                name="smsNotificationsEnabled"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Checkbox
                      checked={value}
                      disabled={status === ProfileStatus.inactive}
                      disabledLegend="To update SMS notifications, you must activate the patient first"
                      error={errors.smsNotificationsEnabled?.message}
                      id="sms-notifications"
                      onChange={onChange}
                    />
                  );
                }}
              />
            ) : (
              <span>{profile?.smsEnabledAt ? "Yes" : "No"}</span>
            )}
          </DataRow>

          <DataRow title="Last Session">
            {profile?.lastSession ? formatLongDate(profile.lastSession) : "-"}
          </DataRow>

          {profile?.preferences ? (
            <DataRow title="Preferences">
              <div className="flex flex-col gap-y-2">
                {typedObjectEntries(profile.preferences).map(([key, value]) => {
                  const options = typedObjectEntries(value).map(([optionKey]) => {
                    return { label: optionKey, value: optionKey };
                  });

                  return (
                    <SelectableAccordion
                      disabled={!isEditing}
                      id={key}
                      key={key}
                      onChange={(selected) => {
                        setValue(`preferences.${key}`, selected);
                      }}
                      onSelectAll={(checked) => {
                        setValue(
                          `preferences.${key}`,
                          checked
                            ? options.map(({ label }) => {
                                return label;
                              })
                            : [],
                        );
                      }}
                      options={options}
                      selected={currentPreferences?.[key] ?? []}
                      title={PREFERENCE_LABELS[key] ?? key}
                    />
                  );
                })}
              </div>
            </DataRow>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export const getDisabledClasses = (isDisabled?: boolean) => {
  return {
    className:
      "w-full disabled:border-none disabled:bg-transparent disabled:p-0 disabled:text-content-strong",
    wrapperClassName: isDisabled ? "opacity-100 shadow-none" : undefined,
  };
};
